import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { registry, type RegAsset } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { Select, SearchInput } from "@/components/inputs";
import { ago, dateTime } from "@/lib/format";
import { kindLabel, useTemplates } from "@/modules/asset_catalog/common";
import { AssetState, CustodySection, TransferAsset, DriverSection, SlotRow, MembersSection } from "@/modules/registry/AssetsInventory";
import { PART_CHIP, RQ, useCompanies } from "@/modules/registry/parts_common";
import { useCompanyScope } from "@/auth/companyScope";
import { CollapsibleSection } from "@/components/Section";
import { StatusChip } from "@/components/StatusChip";

import { Icon } from "@/components/icons";
/** Pair & repair — ONE asset, full width: what is fitted (install / remove),
 *  which company owns it, and which driver holds it. Single-subject page
 *  (picker + inline sections, the Edit Driver grammar); the Inventory list's
 *  "Open →" button deep-links here with ?id=. This work needs room — it used
 *  to live in a drawer and every row wrapped. */
function SubBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    // TOKENS, NOT TYPED-IN NUMBERS. `--raised` is the surface one step in
    // from `--surface` (the panel), `--line` is the panel's own border, and
    // the title is `.section-label flush` -- the console's section heading,
    // 10px/800/1.8px/`--t-dim`. The first version of this box invented
    // `10.5px / 0.6px / --t-mute`, which matched nothing else on the page.
    <div style={{
      background: "var(--raised)", border: "1px solid var(--line)",
      borderRadius: 10, padding: "4px 14px 10px", marginBottom: 12,
    }}>
      <div className="section-label flush" style={{ marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

export function RegistryPairRepairPage() {
  const qc = useQueryClient();
  const tpl = useTemplates().data;
  const [params, setParams] = useSearchParams();
  const sel = params.get("id");
  const allCompanies = useCompanies().data?.companies ?? [];

  const detail = useQuery({ queryKey: [...RQ, "asset", sel], queryFn: () => registry.asset(sel!), enabled: !!sel });
  const refresh = () => qc.invalidateQueries({ queryKey: [...RQ] });
  const d = detail.data;

  return (
    <div className="mpage on">
      <PageHead eyebrow="Registry · Pair & repair" title={sel ? "Pair & repair" : "Pick an asset"}
        desc="Everything you do to ONE asset: fit or remove subcomponents, move it between companies, and pair it with the driver who holds it." />
      {!sel ? <AssetPicker onPick={(id) => setParams({ id })} />
        : detail.isLoading ? <div className="panel panel-pad cap">Loading…</div>
        : !d ? <div className="panel panel-pad" style={{ color: "var(--bad)", fontSize: 13 }}>
            Asset <span className="mono">{sel}</span> not found.{" "}
            <button className="btn ghost" onClick={() => setParams({})}>Pick another</button>
          </div>
        : (<>
          {/* THE HEADER ANSWERS "what am I looking at", INCLUDING ITS STATE.
              It used to carry only the id, type and model, so the current
              owner, custodian, lifecycle state and key-holder could only be
              learned by reading four separate panels below - and two of them
              only stated it inside a CONTROL's label. A strip of chips is the
              asset's status line: read it, then act. */}
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <span className="mono" style={{ fontWeight: 700, fontSize: 16 }}>{d.asset_id}</span>
              <span className="cap">
                {kindLabel(tpl, d.asset_type)} · {d.model ?? "no model"}
                {d.label ? ` · ${d.label}` : ""}
              </span>
              <button className="btn ghost" style={{ marginLeft: "auto" }} onClick={() => setParams({})}>
                <Icon name="swap" /> Switch asset
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
              <StatusChip st={d.state} map={PART_CHIP} />
              <span className="cap">owner <strong>{d.tenant ?? "In stock"}</strong></span>
              <span className="cap">·</span>
              <span className="cap">held by <strong>{d.custodian ?? d.tenant ?? "In stock"}</strong></span>
              <span className="cap">·</span>
              {/* A slot holds a LIST of installed parts and carries its own
                  `ok` against the catalog BOM, so "filled" is the slot having
                  anything in it and the warning is any slot the catalog is not
                  satisfied by - which is the thing worth seeing from here. */}
              <span className="cap">
                parts <strong>{d.slots.filter((s) => s.installed.length > 0).length}/{d.slots.length}</strong>
                {d.slots.some((s) => !s.ok) &&
                  <span style={{ color: "var(--warn)" }}> · BOM incomplete</span>}
              </span>
              <span className="cap">·</span>
              <span className="cap">enrolled {dateTime(d.created_at)}</span>
            </div>
          </div>

          {/* THE THREE WRITE DOORS (REGISTRY.md section 10) ARE ONE CARD, THREE
              ROWS. They were three stacked panels, which read as three
              unrelated tasks and made the page nine boxes of identical weight.
              They are three changes to ONE subject, so they belong in one
              state table: each row is [what it is now] [what to change it to]
              [commit]. Keeping them as separate COMPONENTS is deliberate -
              moving an asset to `repair` is not the same act as selling it to
              the repairer, and each still owns its own mutation and guard. */}
          <div className="section-label">Ownership, custody and state</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            {/* TWO sub-boxes, not three: ownership and custody are both "who
                has it" and are read together (you transfer, and tick whether
                custody follows). State is a different question about the same
                asset, so it gets its own box rather than a third divider. */}
            <SubBox title="Who owns it, and who has it">
              <TransferAsset asset={d} companies={allCompanies} onChanged={refresh} bare />
              <CustodySection asset={d} companies={allCompanies} onChanged={refresh} bare />
            </SubBox>
            <SubBox title="What stage it is at">
              <AssetState asset={d} onChanged={refresh} bare />
            </SubBox>
          </div>

          {/* ONE CARD FOR DRIVERS, because the console listed the SAME PERSON
              TWICE - once in the roster badged "has the key", once again as
              the key holder. That is what made the page look careless.
              D-75 IS UNTOUCHED: a vehicle is shared by a shift roster (many
              members, exactly one holder), and adding a member must never
              disturb whoever is riding - so "add to roster" and "hand over the
              key" are still two separate actions. They are no longer two
              boxes, and the holder is named once. */}
          <div className="section-label">Drivers - who may drive, and who holds the key</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            {/* The heading names two facts, so the box shows two. D-75: the
                ROSTER and the KEY are separate things, and a shared vehicle
                has many of the first and exactly one of the second. */}
            <SubBox title="Who may drive it">
              <MembersSection asset={d} onChanged={refresh} bare />
            </SubBox>
            <SubBox title="Who holds the key right now">
              <DriverSection asset={d} onChanged={refresh} bare compact />
            </SubBox>
          </div>

          <div className="section-label">Bill of materials - installed vs catalog</div>
          <div className="panel panel-pad" style={{ marginBottom: 14 }}>
            {d.slots.map((s) => <SlotRow key={s.slot} asset={d} slot={s} onChanged={refresh} />)}
          </div>

          {/* HISTORY IS ONE CLICK AWAY, NEVER SCROLLING PAST YOU - the rule
              CollapsibleSection was built for and this page ignored, paying
              for it in a long scroll of rows nobody reads on most visits. */}
          {d.history.length > 0 && (
            <CollapsibleSection title="Installation history" count={d.history.length}>
              {d.history.map((h, n) => (
                <KV key={n} k={h.slot} accent={h.valid_to ? "var(--t-mute)" : "var(--ok)"}
                  v={`${dateTime(h.valid_from)} → ${h.valid_to ? dateTime(h.valid_to) + (h.reason ? ` (${h.reason})` : "") : "now"}${h.actor ? ` · ${h.actor}` : ""}`} />
              ))}
            </CollapsibleSection>
          )}
        </>)}
    </div>
  );
}

/** Same filters as Inventory → Assets, but picking lands you on the work page. */
function AssetPicker({ onPick }: { onPick: (id: string) => void }) {
  const tpl = useTemplates().data;
  const [type, setType] = useState("");
  const { company } = useCompanyScope();   // the app bar's (D-134)
  const [search, setSearch] = useState("");
  const q = useQuery({
    queryKey: [...RQ, "assets", "picker", type, company, search],
    queryFn: () => registry.assets({ asset_type: type || undefined, company: company || undefined,
      search: search || undefined, limit: "500" }),
  });
  const cols = useMemo<DataCol<RegAsset>[]>(() => [
    { key: "id", priority: 1, label: "Asset ID", width: 180, mono: true, render: (a) => <span style={{ fontWeight: 600 }}>{a.asset_id}</span> },
    { key: "type", priority: 2, label: "Type", width: 90, render: (a) => kindLabel(tpl, a.asset_type) },
    { key: "model", priority: 2, label: "Model", width: 200, render: (a) => a.model ?? <span style={{ color: "var(--bad)" }}>no model</span> },
    { key: "label", priority: 2, label: "Label", width: 150, render: (a) => a.label ?? "—" },
    { key: "company", priority: 2, label: "Company", width: 160, render: (a) => a.tenant ?? <span className="muted">In stock</span> },
    { key: "parts", priority: 3, label: "Parts", width: 70, mono: true, render: (a) => a.parts_installed },
    { key: "created", priority: 3, label: "Enrolled", width: 90, mono: true, render: (a) => ago(a.created_at), title: (a) => dateTime(a.created_at) },
  ], [tpl]);
  return (<>
    <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
      <SearchInput placeholder="Search asset id / label…" value={search}
        onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
      <Select value={type} onChange={(e) => setType(e.target.value)}>
        <option value="">All types</option><option value="vehicle">Vehicle</option><option value="battery">Battery</option>
      </Select>
    </div>
    <DataTable rows={q.data?.assets ?? []} cols={cols} rowKey={(r) => r.asset_id} pageSize={100}
      onRowClick={(r) => onPick(r.asset_id)}
      empty={<div className="panel panel-pad cap">
        No assets - enrol one on the Enroll page (What = Asset).
      </div>} />
  </>);
}
